import { Component, type ErrorInfo, type ReactNode } from "react";

import { ErrorBanner } from "@/components/common/ErrorBanner";

interface RouteErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode | ((error: Error) => ReactNode);
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetKey?: string;
}

interface RouteErrorBoundaryState {
  error: Error | null;
}

export function routeErrorMessage(error: unknown, fallback = "Something went wrong.") {
  return error instanceof Error ? error.message : fallback;
}

export function RouteErrorFallback({ error, message = "Could not load this page." }: { error?: unknown; message?: string }) {
  return (
    <div className="p-6">
      <ErrorBanner message={routeErrorMessage(error, message)} />
    </div>
  );
}

export class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(previousProps: RouteErrorBoundaryProps) {
    if (this.state.error && previousProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      if (typeof this.props.fallback === "function") {
        return this.props.fallback(this.state.error);
      }
      return this.props.fallback ?? <RouteErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}
